import React from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
} from './Icons';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'success' | 'warning' | 'error' | 'info';
  title?: string;
  onClose?: () => void;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

const variantClasses: Record<string, string> = {
  success: 'bg-success-50 border-success-200 text-success-700',
  warning: 'bg-warning-50 border-warning-200 text-warning-700',
  error: 'bg-error-50 border-error-200 text-error-700',
  info: 'bg-info-50 border-info-200 text-info-700',
};

const iconComponents: Record<string, React.FC<{ className?: string }>> = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationCircleIcon,
  info: InformationCircleIcon,
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  onClose,
  icon,
  action,
  className = '',
  children,
  ...props
}) => {
  const DefaultIcon = iconComponents[variant];
  const displayIcon =
    icon !== undefined ? icon : <DefaultIcon className="w-5 h-5" />;

  const classes = [
    'p-4 rounded-lg border flex',
    variantClasses[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role="alert" {...props}>
      {displayIcon && <div className="flex-shrink-0 mr-3">{displayIcon}</div>}
      <div className="flex-1">
        {title && <h3 className="font-medium mb-1">{title}</h3>}
        <div className={title ? 'text-sm' : ''}>{children}</div>
      </div>
      {(action || onClose) && (
        <div className="flex-shrink-0 ml-3 flex items-start space-x-2">
          {action}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex rounded-md p-1.5 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
              aria-label="Dismiss"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Alert;
